import * as React from 'react';
import { createContext, useCallback, useContext, useLayoutEffect } from 'react';
import { useLocalStorageState } from '../../utils/useLocalStorageState';
import { ThemePreference } from '../../types/models';

/**
 * App-wide UI theme.
 *
 * PatternFly 6 enables dark theme by adding the `pf-v6-theme-dark` class to the
 * `<html>` element (see the PF6 dark-theme handbook); this provider owns that
 * single class. The chosen preference is cached in localStorage under
 * THEME_STORAGE_KEY. To avoid a flash of the wrong theme, an inline script in
 * `src/index.html` reads that same key and applies the class before the app bundle
 * runs; this provider then keeps the class in sync as the preference changes, via a
 * pre-paint `useLayoutEffect`. Both paths must agree on the key and class name.
 * The backend (`/users/me/preferences`) remains the source of truth — the
 * Preferences tab reconciles this cache with the server on open and on save.
 *
 * `system` resolves to dark/light via the `prefers-color-scheme` media query and
 * live-updates when the OS setting changes.
 */

const THEME_STORAGE_KEY = 'ancalagon-manager.ui.theme';
const PF_DARK_CLASS = 'pf-v6-theme-dark';
const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';

function isValidTheme(value: unknown): value is ThemePreference {
  return value === 'system' || value === 'light' || value === 'dark';
}

interface ThemeContextValue {
  /** The user's stored preference (`system` | `light` | `dark`). */
  themePreference: ThemePreference;
  /** Update the preference — applies to `<html>` and persists to localStorage immediately. */
  setThemePreference: (value: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [stored, setStored] = useLocalStorageState<ThemePreference>(THEME_STORAGE_KEY, 'system');
  // useLocalStorageState does not validate shape; guard against a corrupt value.
  const themePreference: ThemePreference = isValidTheme(stored) ? stored : 'system';

  // Apply the resolved theme to <html> whenever the preference changes, and —
  // while on `system` — whenever the OS colour scheme flips. useLayoutEffect (not
  // useEffect) so the class lands before the browser paints, avoiding a flash on
  // change/mount. `matchMedia` is feature-detected so this degrades gracefully in
  // environments that lack it (older browsers, jsdom).
  useLayoutEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }
    const root = document.documentElement;
    const media =
      typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia(DARK_SCHEME_QUERY)
        : null;

    const apply = () => {
      const systemPrefersDark = media ? media.matches : false;
      const isDark = themePreference === 'dark' || (themePreference === 'system' && systemPrefersDark);
      root.classList.toggle(PF_DARK_CLASS, isDark);
    };

    apply();

    // Only the `system` preference needs to react to OS colour-scheme changes.
    if (themePreference === 'system' && media) {
      media.addEventListener('change', apply);
      return () => media.removeEventListener('change', apply);
    }
    return undefined;
  }, [themePreference]);

  const setThemePreference = useCallback(
    (value: ThemePreference) => {
      setStored(isValidTheme(value) ? value : 'system');
    },
    [setStored],
  );

  return <ThemeContext.Provider value={{ themePreference, setThemePreference }}>{children}</ThemeContext.Provider>;
};

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
